// src/components/Apm/Inv/Invs.js

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Alert, useMediaQuery, useTheme } from '@mui/material';

import TableBasic from '../Tables/TableBasic';
import { columnsInvs } from '../Tables/Columns/columnsInvs';
import { useGetInvsQuery } from '../../../redux/features/invApi';
import useDocumentListQuery from '../../../hooks/use-document-list-query';

const INVOICE_ORDERING_FIELDS = {
  vn: 'vn',
  date_inv: 'date_inv',
  bill_to: 'bill_to__companyname',
  load_detail: 'load_detail',
  load_address: 'load_address',
  unload_address: 'unload_address',
  status: 'status__order_number',
};

const serializeInvoiceSearch = (values) => ({
  quote: false,
  bill_to: values?.bill_to?.uf || values?.bill_to?.id || undefined,
  load_detail: values?.load_detail || undefined,
  load_address: values?.load_address || undefined,
  unload_address: values?.unload_address || undefined,
  country_load: values?.loading_country?.value || undefined,
  country_unload: values?.unloading_country?.value || undefined,
  date_min: values?.date_min || undefined,
  date_max: values?.date_max || undefined,
  invoice_from: values?.invoice_from || undefined,
  invoice_to: values?.invoice_to || undefined,
  iteminv_invs: values?.iteminv_invs?.id || undefined,
  currency: values?.currency?.id || undefined,
  comment_invs: values?.comment_invs || undefined,
  status: values?.status?.serial_number || undefined,
});

const Invs = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [updatedColumns, setUpdatedColumns] = useState(columnsInvs());
  const { searchInv } = useSelector((state) => state.searchInvSlice);

  const filters = useMemo(() => serializeInvoiceSearch(searchInv), [searchInv]);
  const tableState = useDocumentListQuery(filters, INVOICE_ORDERING_FIELDS);

  const queryParams = useMemo(
    () => ({
      ...filters,
      ...tableState.paginationParams,
    }),
    [filters, tableState.paginationParams]
  );

  const { data: invoicePage, isFetching, isError, error, refetch: refetchInvs } = useGetInvsQuery(queryParams);

  const invoices = invoicePage?.results ?? [];
  const totalCount = invoicePage?.count ?? 0;

  useEffect(() => {
    tableState.ensureValidPage(totalCount);
  }, [tableState.ensureValidPage, totalCount, tableState]);

  useEffect(() => {
    const allColumns = columnsInvs().map((column) => ({
      ...column,
      disableSorting: column.disableSorting || !INVOICE_ORDERING_FIELDS[column.id],
    }));

    if (smMediaDown) {
      setUpdatedColumns(
        allColumns.filter(
          (column) =>
            column.id === 'vn' ||
            column.id === 'date_inv' ||
            column.id === 'amount' ||
            column.id === 'bill_to' ||
            column.id === 'actions'
        )
      );

      return;
    }

    setUpdatedColumns(allColumns);
  }, [smMediaDown]);

  if (isError) {
    return <Alert severity="error">{error?.data?.detail || t('toast.invoice_list_not_updated')}</Alert>;
  }

  return (
    <TableBasic
      columns={updatedColumns}
      items={invoices}
      totalCount={totalCount}
      resourceType="invoice"
      isFetching={isFetching}
      refetchItems={refetchInvs}
      page={tableState.page}
      rowsPerPage={tableState.rowsPerPage}
      order={tableState.order}
      orderBy={tableState.orderBy}
      quickSearchInput={tableState.quickSearchInput}
      onQuickSearchChange={tableState.setQuickSearchInput}
      onPageChange={tableState.handlePageChange}
      onRowsPerPageChange={tableState.handleRowsPerPageChange}
      onSortChange={tableState.handleSortChange}
    />
  );
};

export default Invs;
